/**
 * UpdatePrompt — Soft toast prompting users to update when a new version is detected.
 *
 * Renders nothing itself — uses Sonner toast (already mounted in App.tsx)
 * to show the prompt. This component manages the toast lifecycle:
 * shows when updateAvailable becomes true, dismisses when user acts.
 *
 * Platform-aware:
 *   - Web: "Reload now" → window.location.reload()
 *   - iOS/Android: "Update" → opens App Store / Play Store
 *
 * @see VERSION_UPDATE_PLAN.md — Fas 2.4 Soft Prompt
 */

import { useEffect, useRef } from "react"
import { useTranslation } from "react-i18next"
import { Capacitor } from "@capacitor/core"
import { toast } from "sonner"
import type { VersionCheckState } from "@/app/hooks/useVersionCheck"

interface UpdatePromptProps {
  versionCheck: VersionCheckState
}

const AUTO_DISMISS_MS = 30_000 // 30 seconds

export function UpdatePrompt({ versionCheck }: UpdatePromptProps) {
  const { t } = useTranslation()
  const { updateAvailable, latestVersion, handleUpdate, handleDismiss } =
    versionCheck
  const toastIdRef = useRef<string | number | undefined>(undefined)
  const shownVersionRef = useRef<string | null>(null)

  useEffect(() => {
    if (!updateAvailable || !latestVersion) {
      // Dismiss any existing toast if update is no longer available
      if (toastIdRef.current !== undefined) {
        toast.dismiss(toastIdRef.current)
        toastIdRef.current = undefined
        shownVersionRef.current = null
      }
      return
    }

    // Don't re-show for the same version
    if (shownVersionRef.current === latestVersion) return
    shownVersionRef.current = latestVersion

    const isWeb = Capacitor.getPlatform() === "web"
    const actionLabel = isWeb ? t("Reload now") : t("Update")

    toastIdRef.current = toast(t("A new version of ToToDo is available"), {
      description: `v${latestVersion}`,
      duration: AUTO_DISMISS_MS,
      action: {
        label: actionLabel,
        onClick: () => {
          toastIdRef.current = undefined
          shownVersionRef.current = null
          handleUpdate()
        },
      },
      cancel: {
        label: t("Not now"),
        onClick: () => {
          toastIdRef.current = undefined
          shownVersionRef.current = null
          handleDismiss()
        },
      },
      onDismiss: () => {
        toastIdRef.current = undefined
        // Don't reset shownVersionRef — prevents re-showing after auto-dismiss
      },
    })

    return () => {
      if (toastIdRef.current !== undefined) {
        toast.dismiss(toastIdRef.current)
        toastIdRef.current = undefined
      }
    }
  }, [updateAvailable, latestVersion, handleUpdate, handleDismiss, t])

  // This component renders nothing — all UI is via Sonner toast
  return null
}
